import { useMemo } from 'react';
import {
  FieldType,
  MultiSelectField,
  SourceType,
} from 'components/organisms/tasks/fields';
import { useIsCurrentUserPartner } from 'hooks/use-partner';
import { useTranslation } from 'hooks/use-translation';
import { useGetAllPartnerCompanies } from 'queries/partner';
import { byLabel } from 'utils/comparators/by-label';
import { getRandomColor } from '../get-random-color';

export const usePartnerCompanyField = (
  fieldId: number,
): {
  partnerCompaniesField: MultiSelectField;
  isLoading: boolean;
  isAnyPartnerCompanyAvailable: boolean;
} => {
  const {
    data: companies,
    isLoading,
    isInitialLoading,
  } = useGetAllPartnerCompanies();
  const fieldLabel = useTranslation('partners');
  const isAnyPartnerCompanyAvailable = (companies?.length ?? 0) > 0;
  const isCurrentUserPartner = useIsCurrentUserPartner();

  return useMemo(
    () => ({
      partnerCompaniesField: {
        fieldId,
        fieldName: 'partnerCompany',
        fieldLabel,
        type: FieldType.MultiSelect,
        customField: false,
        private: false,
        metaData: {
          options: (companies ?? [])
            .filter((company) => company.active === true)
            .map((company) => ({
              label: company.companyName,
              value: company.companyId,
              imageUrl: company.companyLogoUrl,
              active: true,
              color: getRandomColor(company.companyId),
            }))
            .sort(byLabel),
        },
        sourceType: SourceType.Project,
      },
      isLoading: !isCurrentUserPartner ? isLoading : isInitialLoading,
      isAnyPartnerCompanyAvailable,
    }),
    [
      companies,
      fieldId,
      fieldLabel,
      isAnyPartnerCompanyAvailable,
      isLoading,
      isCurrentUserPartner,
      isInitialLoading,
    ],
  );
};
